/**
 * Rectangle table shape — used for 6' tables, 8' tables, and head tables.
 * Head tables are single-sided (chairs only on one long edge, facing the
 * room) per PRD §7.4 step 4; regular rectangle tables seat guests on both
 * long edges.
 */

import { Group, Rect, Circle, Text } from 'react-konva';
import { useState } from 'react';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

interface RectTableProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  /** See ObjectRenderer.tsx and RoundTable.tsx for why this exists —
   * counter-rotates the label only, against the room's view orientation. */
  roomRotationDegrees: number;
}

export function RectTable({ object, catalogEntry, isSelected, onSelect, onDragEnd, roomRotationDegrees }: RectTableProps) {
  const widthPx = catalogEntry.defaultWidthFt * FEET_TO_PX;
  const heightPx = catalogEntry.defaultHeightFt * FEET_TO_PX;
  const singleSided = object.type === 'head_table';
  const seats = object.seats ?? catalogEntry.defaultSeats ?? 0;
  // See RoundTable.tsx for why shadow rendering is suppressed during drag.
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(true);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grabbing';
  }

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(false);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grab';
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  // Distribute chairs along the long edge(s). Single-sided tables (head
  // table) put every chair on one side, facing into the room.
  const chairPositions: { x: number; y: number }[] = [];
  if (singleSided) {
    for (let i = 0; i < seats; i++) {
      const t = (i + 0.5) / seats;
      chairPositions.push({ x: t * widthPx - widthPx / 2, y: heightPx / 2 + 14 });
    }
  } else {
    const perSide = Math.ceil(seats / 2);
    for (let i = 0; i < perSide; i++) {
      const t = (i + 0.5) / perSide;
      chairPositions.push({ x: t * widthPx - widthPx / 2, y: -heightPx / 2 - 14 });
    }
    for (let i = 0; i < seats - perSide; i++) {
      const t = (i + 0.5) / (seats - perSide || 1);
      chairPositions.push({ x: t * widthPx - widthPx / 2, y: heightPx / 2 + 14 });
    }
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {chairPositions.map((pos, i) => (
        <Circle key={i} x={pos.x} y={pos.y} radius={6} fill="#FBF8F2" stroke="#C9A35C" strokeWidth={1} />
      ))}

      <Rect
        x={-widthPx / 2}
        y={-heightPx / 2}
        width={widthPx}
        height={heightPx}
        fill="#FFFEFC"
        stroke={isSelected ? '#C9A35C' : '#8FA38B'}
        strokeWidth={isSelected ? 3 : 1.5}
        cornerRadius={2}
        shadowColor="#2A2522"
        shadowOpacity={isDragging ? 0 : 0.12}
        shadowBlur={isDragging ? 0 : 6}
        shadowOffsetY={isDragging ? 0 : 2}
      />

      <Text
        text={object.label}
        fontSize={11}
        fontFamily="Inter, sans-serif"
        fill="#4D453C"
        width={widthPx}
        height={heightPx}
        offsetX={widthPx / 2}
        offsetY={heightPx / 2}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {object.locked && (
        <Text
          text={'\u{1F512}'}
          fontSize={13}
          x={0}
          y={-(heightPx / 2 + 14 + 14)}
          offsetX={6.5}
          offsetY={6.5}
          listening={false}
        />
      )}
    </Group>
  );
}
